// src/components/digital_twin/SimulationImpact3DLayer.js
// --- UPDATE: v1.0 — renders simulation results into the Digital Twin scene
//   • Damage / impact markers (one InstancedMesh, per-instance colour by state)
//   • Flood columns (InstancedMesh, height = flood depth × exaggeration)
//   • Hurricane track line, moving eye and radius-of-max-wind ring
//   • Playback hour only rewrites instance matrices — no geometry rebuilds
//
// Positioning: asset markers sit exactly on the existing scene object for that
// asset (group 'assets', userData.assetId). For anything else (storm track,
// assets not drawn) the layer calibrates lat/lng → scene units from those same
// asset objects, falling back to the MercatorCoordinate convention used by
// Anomalies3DLayer. This keeps the overlay aligned whatever scale the twin uses.
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { MercatorCoordinate } from 'mapbox-gl';

const MAX_INSTANCES = 6000;
const FLOOD_EXAGGERATION = 6;
const STATE_COLORS = {
  critical: new THREE.Color(0xe53e3e),
  warning: new THREE.Color(0xed8936),
  ok: new THREE.Color(0x48bb78),
};
const DAMAGE_COLORS = {
  complete: new THREE.Color(0x742a2a),
  extensive: new THREE.Color(0xe53e3e),
  moderate: new THREE.Color(0xed8936),
  slight: new THREE.Color(0xecc94b),
  none: new THREE.Color(0x48bb78),
};

const median = (arr) => {
  if (!arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
};

/** Build lat/lng → scene projector calibrated from the scene's own asset objects. */
export function buildProjector(scene, origin, assetsById) { // --- UPDATE (elevation): shared with RunoffFlow3DLayer
  const objectById = new Map();
  const assetsGroup = scene?.getObjectByName('assets');
  const ax = [], ay = [], zs = [];
  for (const obj of assetsGroup?.children || []) {
    const id = obj.userData?.assetId;
    if (id == null) continue;
    objectById.set(String(id), obj);
    if (ax.length >= 40 || !origin) continue;
    const a = assetsById?.get(String(id));
    const lat = Number(a?.latitude), lng = Number(a?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const mc = MercatorCoordinate.fromLngLat([lng, lat]);
    const dx = mc.x - origin.x, dy = mc.y - origin.y;
    if (Math.abs(dx) > 1e-9) ax.push(obj.position.x / dx);
    if (Math.abs(dy) > 1e-9) ay.push(obj.position.y / dy);
    zs.push(obj.position.z);
  }
  const sx = median(ax) ?? 1;
  const sy = median(ay) ?? 1;
  const calibrated = ax.length > 0;
  const baseZ = median(zs) ?? 0;

  const sample = MercatorCoordinate.fromLngLat([-77.35, 25.05]);
  const mpu = typeof sample.meterInMercatorCoordinateUnits === 'function' ? sample.meterInMercatorCoordinateUnits() : 1 / 40075016;
  // Calibrated from scene objects when possible; otherwise follow useDigitalTwin's
  // convention (scene units = Mercator units) whenever origin is a MercatorCoordinate.
  const originMpu = typeof origin?.meterInMercatorCoordinateUnits === 'function' ? origin.meterInMercatorCoordinateUnits() : null;
  const metersToScene = calibrated ? Math.abs(sx) * mpu : (originMpu ?? 1);

  return {
    objectById,
    metersToScene,
    baseZ,
    toScene(lat, lng) {
      if (!origin || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      const mc = MercatorCoordinate.fromLngLat([lng, lat]);
      return new THREE.Vector3((mc.x - origin.x) * sx, (mc.y - origin.y) * sy, baseZ);
    },
  };
}

const disposeGroup = (group) => {
  if (!group) return;
  group.traverse((o) => {
    if (o.geometry) o.geometry.dispose();
    if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => m.dispose());
  });
  while (group.children.length) group.remove(group.children[0]);
};

const SimulationImpact3DLayer = ({
  scene, origin, map, result, scenario, assets = [], stateAt, playbackHour = null, visible = true, onLayerUpdated,
}) => {
  const groupRef = useRef(null);
  const meshesRef = useRef({ markers: null, floods: null, eye: null, ring: null, items: [], floodItems: [], track: null });

  const assetsById = useMemo(() => {
    const m = new Map();
    for (const a of assets) m.set(String(a.id), a);
    return m;
  }, [assets]);
  // Content signature so a new-but-identical assets array does not rebuild meshes
  const assetsSig = useMemo(() => `${assets.length}:${assets.length ? `${assets[0].id}-${assets[assets.length - 1].id}` : ''}`, [assets]);
  const assetsByIdRef = useRef(assetsById);
  assetsByIdRef.current = assetsById;

  // ── Build meshes when the result changes ────────────────────────────────
  useEffect(() => {
    if (!scene) return undefined;
    let group = groupRef.current;
    if (!group) {
      group = new THREE.Group();
      group.name = 'simulationImpact';
      group.renderOrder = 900;
      scene.add(group);
      groupRef.current = group;
    }
    disposeGroup(group);
    meshesRef.current = { markers: null, floods: null, eye: null, ring: null, items: [], floodItems: [], track: null };

    const nodes = result?.affectedNodes || [];
    if (!nodes.length && !result?.timeline?.length) {
      map?.triggerRepaint?.();
      return undefined;
    }

    const assetsById = assetsByIdRef.current;
    const proj = buildProjector(scene, origin, assetsById);
    const m2s = proj.metersToScene;

    // Resolve positions once
    const items = [];
    for (const r of nodes) {
      if (items.length >= MAX_INSTANCES) break;
      const obj = proj.objectById.get(String(r.asset_id));
      let pos = obj ? obj.position.clone() : null;
      if (!pos) {
        const a = assetsById.get(String(r.asset_id));
        pos = proj.toScene(Number(r.latitude ?? a?.latitude), Number(r.longitude ?? a?.longitude));
      }
      if (pos) items.push({ r, pos });
    }

    // Impact markers
    if (items.length) {
      const geom = new THREE.OctahedronGeometry(1, 0);
      const mat = new THREE.MeshStandardMaterial({ emissiveIntensity: 0.6, roughness: 0.4, metalness: 0.1, transparent: true, opacity: 0.92 });
      const markers = new THREE.InstancedMesh(geom, mat, items.length);
      markers.name = 'simulation-markers';
      markers.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      items.forEach(({ r }, i) => {
        const c = r.damage_state ? DAMAGE_COLORS[r.damage_state] : STATE_COLORS[r.state] || STATE_COLORS.ok;
        markers.setColorAt(i, (r.damage_state === 'none' && r.state !== 'ok') ? STATE_COLORS[r.state] : c);
      });
      if (markers.instanceColor) markers.instanceColor.needsUpdate = true;
      group.add(markers);
      meshesRef.current.markers = markers;
      meshesRef.current.items = items;
    }

    // Flood columns
    const floodItems = items.filter(({ r }) => (r.peak_flood_depth_m || r.inundation_depth_m || 0) > 0.05);
    if (floodItems.length) {
      const geom = new THREE.CylinderGeometry(1, 1, 1, 12, 1, true);
      geom.rotateX(Math.PI / 2); // cylinder axis → scene Z (up)
      const mat = new THREE.MeshStandardMaterial({ color: 0x3182ce, emissive: 0x2b6cb0, emissiveIntensity: 0.35, transparent: true, opacity: 0.45, side: THREE.DoubleSide, depthWrite: false });
      const floods = new THREE.InstancedMesh(geom, mat, floodItems.length);
      floods.name = 'simulation-floods';
      floods.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      group.add(floods);
      meshesRef.current.floods = floods;
      meshesRef.current.floodItems = floodItems;
    }

    // Storm track
    const centers = (result?.timeline || []).filter(t => t.storm_center);
    if (centers.length > 1) {
      const pts = centers.map(t => proj.toScene(t.storm_center.lat, t.storm_center.lng)).filter(Boolean)
        .map(p => p.setZ(p.z + 40 * m2s));
      if (pts.length > 1) {
        const line = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints(pts),
          new THREE.LineDashedMaterial({ color: 0xd53f8c, dashSize: 400 * m2s, gapSize: 250 * m2s, transparent: true, opacity: 0.85 })
        );
        line.computeLineDistances();
        line.name = 'simulation-track';
        group.add(line);

        const eye = new THREE.Mesh(new THREE.SphereGeometry(250 * m2s, 16, 16),
          new THREE.MeshStandardMaterial({ color: 0xd53f8c, emissive: 0xd53f8c, emissiveIntensity: 0.9, transparent: true, opacity: 0.9 }));
        eye.name = 'simulation-eye';
        group.add(eye);

        const rmwKm = result?.parameters?.storm?.rmw_km || 40;
        const ring = new THREE.Mesh(new THREE.RingGeometry(rmwKm * 1000 * m2s * 0.97, rmwKm * 1000 * m2s, 64),
          new THREE.MeshBasicMaterial({ color: 0xd53f8c, transparent: true, opacity: 0.35, side: THREE.DoubleSide, depthWrite: false }));
        ring.name = 'simulation-rmw';
        group.add(ring);

        meshesRef.current.eye = eye;
        meshesRef.current.ring = ring;
        meshesRef.current.track = { centers, pts };
      }
    }

    meshesRef.current.m2s = m2s;
    return undefined;
  // origin compared by value — a new {x,y} object each render must not rebuild the meshes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, origin?.x, origin?.y, result, assetsSig, map]);

  // ── Update instance transforms for playback hour / visibility ───────────
  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    group.visible = !!visible;
    const { markers, floods, items, floodItems, eye, ring, track, m2s = 1 } = meshesRef.current;
    const dummy = new THREE.Object3D();

    if (markers) {
      items.forEach(({ r, pos }, i) => {
        const s = stateAt ? stateAt(r, playbackHour) : r.state;
        const show = s && s !== 'ok';
        const impact = Math.max(0.15, r.impact_score ?? 0.3);
        dummy.position.set(pos.x, pos.y, pos.z + 30 * m2s);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.setScalar(show ? (8 + 14 * impact) * m2s : 0);
        dummy.updateMatrix();
        markers.setMatrixAt(i, dummy.matrix);
      });
      markers.instanceMatrix.needsUpdate = true;
    }

    if (floods) {
      floodItems.forEach(({ r, pos }, i) => {
        let depth = r.peak_flood_depth_m ?? r.inundation_depth_m ?? 0;
        let active = true;
        // --- UPDATE (elevation): run-off results carry the hourly ponding depth — the column rises and drains
        if (playbackHour != null && Array.isArray(r.hourly_depth_m)) {
          depth = r.hourly_depth_m[Math.max(0, Math.min(r.hourly_depth_m.length - 1, Math.round(playbackHour)))] || 0;
          active = depth >= 0.05;
        } else
        // --- END UPDATE
        if (playbackHour != null && r.time_to_flood_hours != null) {
          active = playbackHour >= r.time_to_flood_hours && playbackHour <= r.time_to_flood_hours + (r.hours_flooded || 0);
        }
        const h = Math.max(0.2, depth) * FLOOD_EXAGGERATION * m2s;
        dummy.position.set(pos.x, pos.y, pos.z + h / 2);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.set(active ? 18 * m2s : 0, active ? 18 * m2s : 0, active ? h : 0);
        dummy.updateMatrix();
        floods.setMatrixAt(i, dummy.matrix);
      });
      floods.instanceMatrix.needsUpdate = true;
    }

    if (eye && ring && track) {
      const { centers, pts } = track;
      let idx = pts.length - 1;
      if (playbackHour != null) {
        let best = Infinity;
        centers.forEach((c, i) => { const d = Math.abs(c.hour - playbackHour); if (d < best) { best = d; idx = i; } });
      } else {
        // closest approach = hour nearest 0
        let best = Infinity;
        centers.forEach((c, i) => { const d = Math.abs(c.hour); if (d < best) { best = d; idx = i; } });
      }
      const p = pts[Math.min(idx, pts.length - 1)];
      eye.position.copy(p);
      ring.position.copy(p);
    }

    map?.triggerRepaint?.();
    onLayerUpdated?.();
  }, [playbackHour, visible, stateAt, result, scenario, map, onLayerUpdated]);

  // ── Cleanup ─────────────────────────────────────────────────────────────
  useEffect(() => () => {
    const group = groupRef.current;
    if (group) {
      disposeGroup(group);
      group.parent?.remove(group);
    }
    groupRef.current = null;
  }, []);

  return null;
};

export default SimulationImpact3DLayer;
// --- END UPDATE